import React, { useEffect, useState } from "react";
import { X, Award, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from "recharts";
import { api, formatTL, fmtDate } from "@/lib/api";
import { SnapCard, Th } from "./shared";

export default function KidemSimulationModal({ person, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/personnel/${person.id}/kidem-simulation?years_ahead=20`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Simülasyon yüklenemedi"))
      .finally(() => setLoading(false));
  }, [person]);

  const chartData = (data?.projections || []).map((p) => ({
    label: p.label,
    aylik_prim: p.aylik_kidem_primi,
    tazminat: p.kidem_tazminati_toplam,
  }));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-4xl my-8" data-testid="kidem-simulation-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <Award size={18} className="text-emerald-700" />
              Kıdem Simülasyonu · {person.ad_soyad}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {person.pozisyon} · TC {person.sicil_no} · İşe başlama {fmtDate(person.ise_baslama_tarihi)}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          {loading && <div className="text-center py-10 text-slate-400">Simülasyon hesaplanıyor...</div>}
          {!loading && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SnapCard label="Mevcut Kıdem" value={`${data.mevcut_tam_yil} yıl`} tone="slate" />
                <SnapCard label="Aylık Brüt" value={formatTL(data.personnel.aylik_brut)} tone="slate" />
                <SnapCard label="Kıdem Oranı" value={`%${data.kidem_oran_yillik_yuzde} / yıl`} tone="indigo" />
                <SnapCard
                  label="Bugünkü Kıdem Tazminatı"
                  value={formatTL(data.projections[0]?.kidem_tazminati_toplam || 0)}
                  tone="emerald"
                />
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="kidem-projection-table">
                  <thead className="bg-slate-900 text-slate-100">
                    <tr>
                      <Th>Dönem</Th>
                      <Th center>Tam Yıl</Th>
                      <Th right>Aylık Kıdem Primi</Th>
                      <Th right>Yıllık Kıdem Toplamı</Th>
                      <Th right>Kıdem Tazminatı (Toplu)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.projections.map((p, i) => (
                      <tr key={i} className={`border-b border-slate-100 ${i === 0 ? "bg-emerald-50/40" : ""}`} data-testid={`projection-row-${i}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-slate-900">{p.label}</div>
                          <div className="text-xs text-slate-500 font-mono-tab">
                            {new Intl.DateTimeFormat('tr-TR', { month: 'long', year: 'numeric' }).format(new Date(p.yil, p.ay - 1, 1))}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-center font-mono-tab">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                            {p.tam_yil} yıl
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono-tab text-emerald-700 font-semibold">
                          {formatTL(p.aylik_kidem_primi)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono-tab text-slate-700">
                          {formatTL(p.yillik_kidem_toplami)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono-tab font-bold text-slate-900">
                          {formatTL(p.kidem_tazminati_toplam)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp size={14} className="text-slate-700" />
                  <h4 className="font-semibold text-sm">Kıdem Projeksiyonu Grafiği</h4>
                </div>
                <div className="h-64" data-testid="kidem-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: "#64748B", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: "#64748B", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                      />
                      <Tooltip
                        formatter={(v) => formatTL(v)}
                        contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#F8FAFC" }}
                      />
                      <Bar yAxisId="left" dataKey="aylik_prim" name="Aylık Kıdem Primi" fill="#059669" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="tazminat" name="Kıdem Tazminatı (Toplu)" stroke="#DC2626" strokeWidth={3} dot={{ fill: "#DC2626", r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <strong>Formül:</strong> Aylık Kıdem Primi = aylık brüt × %{data.kidem_oran_yillik_yuzde}/yıl × tam yıl ·
                Kıdem Tazminatı (toplu) = aylık brüt × tam yıl (Türkiye standart, SGK tavanı dikkate alınmamıştır).
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
